<script setup lang="ts">
import { useVModel } from '@vueuse/core';
import { ref, watch, type HTMLAttributes } from 'vue';

const props = defineProps<{
  defaultValue?: string | number;
  modelValue?: string | number;
  value?: string | number;
  type?: string;
  // 在输入阶段对原始文本进行转换（提交前）。返回用于触发 v-model 的值
  inputTransform?: (raw: string) => string | number;
  // 在提交时机（change/blur）对当前值进行转换。返回最终提交的值
  commitTransform?: (raw: string | number) => string | number;
  // 将值格式化为展示用文本
  formatter?: (val: string | number) => string | number;
  class?: HTMLAttributes['class'];
  id?: HTMLAttributes['id'];
}>();

const emits = defineEmits<{
  (e: 'update:modelValue', payload: string | number): void;
  (e: 'change', payload: string | number): void;
}>();

const innerModel = useVModel(props, 'modelValue', emits, {
  passive: true,
  defaultValue: props.defaultValue,
});
const inputRef = ref<HTMLInputElement>();
const focus = () => inputRef.value?.focus();
const blur = () => inputRef.value?.blur();
defineExpose({ focus, blur });

const formatForDisplay = (val: string | number): string | number => {
  return props.formatter ? props.formatter(val) : val;
};

const initial = props.modelValue ?? props.value ?? props.defaultValue ?? '';
const displayValue = ref<string | number>(formatForDisplay(initial));

watch(
  () => [props.modelValue, props.value] as const,
  ([mv, v]) => {
    const next = mv ?? v;
    if (next !== undefined) displayValue.value = formatForDisplay(next);
  },
);

const onInput = (e: Event) => {
  const el = e.target as HTMLInputElement;
  const raw = el.value;
  const next = props.inputTransform ? props.inputTransform(raw) : raw;
  // 输入过程中保留原始文本，保证输入体验自然
  displayValue.value = raw;
  innerModel.value = next;
};

const commitChange = () => {
  let next: string | number = displayValue.value;
  next = props.commitTransform ? props.commitTransform(next) : next;
  displayValue.value = formatForDisplay(next);
  innerModel.value = next;
  emits('change', next);
};
</script>

<template>
  <input
    ref="inputRef"
    :type="type ?? 'text'"
    :value="displayValue"
    data-slot="input"
    :class="class"
    :id="id"
    v-bind="$attrs"
    @input="onInput"
    @change="commitChange"
    @blur="commitChange"
  />
</template>

<style scoped>
/* 统一重置输入框的视觉与交互样式 */
input {
  /* 视觉 */
  appearance: none;
  -webkit-appearance: none;
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
  color: inherit;
  font: inherit;
  line-height: inherit;
  /* 布局 */
  box-sizing: border-box;
  padding: 0;
  margin: 0;
  width: 100%;
  height: 100%;
  /* 交互 */
  outline: none;
}

/* 状态：聚焦与悬停不产生默认高亮 */
input:focus,
input:hover {
  outline: none;
  box-shadow: none;
}

/* 状态：禁用与只读 */
input:disabled {
  cursor: not-allowed;
  opacity: 0.6;
  background: transparent;
  color: inherit;
  -webkit-text-fill-color: currentColor;
}

input[readonly] {
  cursor: default;
}

/* 占位符颜色继承并弱化 */
input::placeholder {
  color: currentColor;
  opacity: 0.5;
}

/* WebKit 自动填充背景与文本颜色统一 */
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus {
  -webkit-box-shadow: 0 0 0px 1000px transparent inset;
  -webkit-text-fill-color: currentColor;
  transition: background-color 5000s ease-in-out 0s;
}

/* 去除数字输入的上下箭头 */
/* input[type='number']::-webkit-outer-spin-button,
input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  appearance: none;
  margin: 0;
}
input[type='number'] {
  -moz-appearance: textfield;
  appearance: textfield;
} */

/* 去除搜索输入默认修饰 */
input[type='search']::-webkit-search-decoration,
input[type='search']::-webkit-search-cancel-button,
input[type='search']::-webkit-search-results-button,
input[type='search']::-webkit-search-results-decoration {
  -webkit-appearance: none;
  appearance: none;
}
/* 去除edge下password携带眼睛图标 */
input[type='password']::-ms-reveal {
  display: none;
}

input[type='password']::-ms-clear {
  display: none;
}
</style>
